import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, money, fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { X, ClockCounterClockwise, ArrowUpRight, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Slide-in drawer that shows the CURRENT USER's own past cash-outs at
 * the active store. Backed by GET /api/cash-outs/history/mine — gated by
 * the `till.cashouts.view_own` permission.
 *
 * Design goals:
 *   • Paginated ("Load 25 more") so a staff who's been at the shop for
 *     years doesn't OOM the browser.
 *   • Optional date range filter — most staff will hit "This week" or
 *     "This month" to reconcile against their own memory.
 *   • Runs off the current store only (store_id header on the API).
 *     Staff who work across two stores need to switch stores first.
 */
export default function MyCashOutsDrawer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = useCallback(async (newSkip = 0, append = false) => {
    setLoading(true);
    try {
      const params = { limit: 25, skip: newSkip };
      if (start) params.start = new Date(start + "T00:00:00").toISOString();
      if (end)   params.end   = new Date(end   + "T23:59:59").toISOString();
      const r = await api.get("/cash-outs/history/mine", { params });
      const rows = r.data?.items || [];
      setItems(append ? (prev) => [...prev, ...rows] : rows);
      setTotal(r.data?.total || 0);
      setTotalAmount(Number(r.data?.total_amount || 0));
      setHasMore(!!r.data?.has_more);
      setSkip(newSkip + rows.length);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  // Load whenever the drawer opens (or filters change while open).
  useEffect(() => {
    if (!open) return;
    load(0, false);
  }, [open, start, end, load]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="my-cashouts-drawer">
      {/* Scrim */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-black/40 backdrop-blur-sm"
        data-testid="my-cashouts-scrim"
      />
      {/* Panel */}
      <div className="w-full max-w-[520px] h-full bg-white shadow-2xl flex flex-col animate-[slideInRight_0.2s_ease-out]">
        <div className="px-5 py-4 border-b border-[#F3F4F6] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] text-[#92400E] flex items-center justify-center">
            <ClockCounterClockwise size={18} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-[16px]">My cash-out history</div>
            <div className="text-[11px] text-[#9CA3AF]">
              Cash you&apos;ve paid out from the till at this store. Only your own entries.
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-[#F3F4F6] flex items-center justify-center"
            data-testid="my-cashouts-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Summary + Filters */}
        <div className="px-5 py-4 border-b border-[#F3F4F6] bg-[#F8FAFC]">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium">Total paid out</div>
              <div className="font-display font-semibold text-[26px] tabular-nums text-[#DC2626]" data-testid="my-cashouts-total-amount">
                ${money(totalAmount)}
              </div>
            </div>
            <div className="text-right text-[11px] text-[#9CA3AF] font-mono" data-testid="my-cashouts-total-count">
              {total.toLocaleString()} entries
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-[#4B5563] flex flex-col gap-1">
              From
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-9 px-3 rounded-lg border border-[#E5E7EB] text-[13px] focus:border-[#1E5FEB] focus:outline-none bg-white"
                data-testid="my-cashouts-start"
              />
            </label>
            <label className="text-[11px] text-[#4B5563] flex flex-col gap-1">
              To
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-9 px-3 rounded-lg border border-[#E5E7EB] text-[13px] focus:border-[#1E5FEB] focus:outline-none bg-white"
                data-testid="my-cashouts-end"
              />
            </label>
          </div>
          {(start || end) && (
            <button
              onClick={() => { setStart(""); setEnd(""); }}
              className="mt-2 text-[11px] text-[#1E5FEB] font-semibold hover:underline"
              data-testid="my-cashouts-clear-dates"
            >
              Clear dates
            </button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto" data-testid="my-cashouts-list">
          {loading && items.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-[#9CA3AF]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#F3F4F6] text-[#9CA3AF] flex items-center justify-center">
                <MagnifyingGlass size={22} weight="bold" />
              </div>
              <div className="font-semibold text-[14px] text-[#121216]">No cash-outs found</div>
              <div className="text-[12px] text-[#9CA3AF] mt-1">
                Nothing matches this date range at the current store.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#F3F4F6]">
              {items.map((c) => (
                <div
                  key={c.id}
                  className="px-5 py-3 flex items-center gap-3 hover:bg-[#F8FAFC]"
                  data-testid={`my-cashout-row-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                    <ArrowUpRight size={14} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] text-[#121216] truncate">{c.category}</div>
                    {c.reason && (
                      <div className="text-[11px] text-[#9CA3AF] truncate">{c.reason}</div>
                    )}
                    <div className="text-[10px] text-[#9CA3AF] font-mono mt-0.5">
                      {fmtDateTime(c.created_at)}
                    </div>
                  </div>
                  <div className="font-mono font-semibold text-[#DC2626] tabular-nums text-[14px]">
                    ${money(c.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#F3F4F6] bg-white flex items-center justify-between text-[12px]">
          <button
            onClick={() => load(0, false)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#E5E7EB] text-[12px] font-semibold hover:border-[#1E5FEB] hover:text-[#1E5FEB] disabled:opacity-50"
            data-testid="my-cashouts-refresh"
          >
            <ArrowClockwise size={12} weight="bold" />
            Refresh
          </button>
          {hasMore ? (
            <button
              onClick={() => load(skip, true)}
              disabled={loading}
              className="font-semibold text-[#1E5FEB] hover:underline disabled:opacity-50"
              data-testid="my-cashouts-load-more"
            >
              {loading ? "Loading…" : `Load 25 more →`}
            </button>
          ) : (
            <span className="text-[11px] text-[#9CA3AF]">
              Showing {items.length} of {total.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
